import React, { useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import StreamWidgets from './StreamWidgets'
import ResearchSummary from './ResearchSummary'
import { useSelector } from 'react-redux'

// Function to format source names for better display
const formatSourceName = (source, type) => {
  // Handle ChromaDB documents that show "research_database"
  if (source === 'research_database' && type === 'chromadb') {
    // For ChromaDB documents, we can't determine the original agent source easily
    // So we'll just show a generic but professional name
    return 'Research Database'
  }
  
  // Handle other known source mappings
  const sourceMapping = {
    'alzheimers_association': "Alzheimer's Association",
    'alzheimers_society': "Alzheimer's Society", 
    'aa_sleep_medicine': 'American Academy of Sleep Medicine',
    'national_sleep_foundation': 'National Sleep Foundation',
    'dementia_uk': 'Dementia UK',
    'nih': 'NIH',
    'pubmed': 'PubMed',
    'arxiv': 'ArXiv',
    'semantic_scholar': 'Semantic Scholar',
    'research_database': 'Research Database'
  }
  
  // Check for exact match (case-insensitive)
  const lowerSource = source.toLowerCase()
  for (const [key, value] of Object.entries(sourceMapping)) {
    if (key.toLowerCase() === lowerSource) {
      return value
    }
  }
  
  // Default formatting: replace underscores with spaces and capitalize
  return source.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

// Custom component to process markdown content with citation links
const MarkdownWithCitations = ({ content, citations = [] }) => {
  // Create a mapping of citation numbers to URLs
  const citationUrls = {}
  citations.forEach((citation, index) => {
    // Use citation.number if available, otherwise use index + 1
    const citationNumber = citation.number || (index + 1).toString()
    if (citation.url) {
      citationUrls[citationNumber] = citation.url
    }
  })
  
  // Debug logging only when citations are available
  if (citations.length > 0) {
    console.log('📚 Citations available:', citations.length, citations)
    console.log('🔗 Citation URLs mapping:', citationUrls)
  }
  
  // Process content to replace citations with JSX elements
  const processContent = (text) => {
    if (!text || typeof text !== 'string') return text
    
    // Handle citations that come after periods and standalone citations
    // Match patterns like: "Text.[1]" or "Text [1]" or just "[1]"
    const citationRegex = /(\[\d+\])/g
    const parts = text.split(citationRegex)
    
    return parts.map((part, index) => {
      const match = part.match(/^\[(\d+)\]$/)
      if (match) {
        const citationNumber = match[1]
        const url = citationUrls[citationNumber]
        
        if (url) {
          return (
            <sup key={index}>
              <a 
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 no-underline ml-0.5"
              >
                [{citationNumber}]
              </a>
            </sup>
          )
        } else {
          return (
            <sup key={index} className="text-gray-500 ml-0.5">
              [{citationNumber}]
            </sup>
          )
        }
      }
      return part
    })
  }
  
  return (
    <div className="prose prose-sm max-w-none">
      <ReactMarkdown
        components={{
          // Process paragraph text to handle citations
          p: ({ children }) => {
            if (typeof children === 'string') {
              return <p className="mb-4">{processContent(children)}</p>
            }
            // For mixed content (text + other elements)
            const processedChildren = React.Children.map(children, child => {
              if (typeof child === 'string') {
                return processContent(child)
              }
              return child
            })
            return <p className="mb-4">{processedChildren}</p>
          },
          // Handle headers with proper spacing
          h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 mt-12 first:mt-0">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-semibold mb-3 mt-12 first:mt-0">{children}</h2>,
          h3: ({ children }) => <h3 className="text-lg font-medium mb-2 mt-8">{children}</h3>,
          // Handle lists
          ul: ({ children }) => <ul className="list-disc pl-5 mb-4">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 mb-4">{children}</ol>,
          // Handle list items with citations
          li: ({ children }) => {
            if (typeof children === 'string') {
              return <li className="mb-1 font-normal">{processContent(children)}</li>
            }
            // For mixed content
            const processedChildren = React.Children.map(children, child => {
              if (typeof child === 'string') {
                return processContent(child)
              }
              return child
            })
            return <li className="mb-1 font-normal">{processedChildren}</li>
          },
          // Handle strong/bold text
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}

export default function ChatMessages({ messages, loading, streamData, wsStatus }) {
  const wsError = useSelector(state => state.websocket.error)
  const messagesEndRef = useRef(null)
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true)
  const scrollPositionRef = useRef(null)
  const isUserScrollingRef = useRef(false)
  
  const scrollToBottom = () => {
    if (autoScrollEnabled && !isUserScrollingRef.current && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }
  
  const scrollToResearchMessage = () => {
    // Only scroll if user hasn't manually scrolled away
    if (isUserScrollingRef.current) return
    
    // Find the "Research in progress..." message and scroll to show it at the top
    const researchMessage = document.querySelector('[data-research-progress]') ||
                            document.querySelector('.loading.loading-dots') ||
                            Array.from(document.querySelectorAll('.text-sm.text-gray-500')).find(el => 
                              el.textContent && el.textContent.includes('Research in progress')
                            )
    
    if (researchMessage) {
      researchMessage.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest' 
      })
    }
  }

  const scrollToFinalAnswer = () => {
    // Only scroll if user hasn't manually scrolled away
    if (isUserScrollingRef.current) return
    
    // Find the final answer by looking for completed assistant messages with content
    const assistantMessages = Array.from(document.querySelectorAll('.bg-base-100.border')).filter(el => {
      // Check if this is an assistant message with actual content (not just "Research in progress...")
      const hasContent = el.querySelector('.prose') && 
                        !el.querySelector('.loading-dots') &&
                        !el.textContent.includes('Research in progress')
      return hasContent
    })
    
    // Get the last assistant message with content (the final answer)
    const finalAnswerElement = assistantMessages[assistantMessages.length - 1]
    
    if (finalAnswerElement) {
      finalAnswerElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest' 
      })
    }
  }
  
  // Auto-scroll logic - only for new messages, not streaming updates
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      
      // Auto-scroll for new user messages
      if (lastMessage.role === 'user') {
        const timeoutId = setTimeout(() => {
          scrollToBottom()
        }, 100)
        return () => clearTimeout(timeoutId)
      }
      // For assistant messages that just started streaming, scroll to research message
      else if (lastMessage.role === 'assistant' && lastMessage.isStreaming && !lastMessage.content) {
        const timeoutId = setTimeout(() => {
          scrollToResearchMessage()
        }, 100)
        return () => clearTimeout(timeoutId)
      }
      // For completed assistant messages, scroll to bottom normally
      else if (lastMessage.role === 'assistant' && !lastMessage.isStreaming) {
        const timeoutId = setTimeout(() => {
          scrollToBottom()
        }, 100)
        return () => clearTimeout(timeoutId)
      }
    }
  }, [messages])
  
  // Handle research progress scrolling
  useEffect(() => {
    // Scroll to research message when research starts
    if (streamData && Object.keys(streamData).length > 0 && !streamData?.final_answer && !streamData?.status) {
      const timeoutId = setTimeout(() => {
        scrollToResearchMessage()
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [streamData])

  // Handle final answer scrolling
  useEffect(() => {
    if (streamData?.final_answer || streamData?.status === 'completed') {
      const timeoutId = setTimeout(() => {
        scrollToFinalAnswer()
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [streamData?.final_answer, streamData?.status])
  
  // Enhanced scroll monitoring with position preservation
  useEffect(() => {
    const chatContainer = document.querySelector('.overflow-y-auto')
    if (!chatContainer) return

    const preserveScrollPosition = () => {
      scrollPositionRef.current = {
        scrollTop: chatContainer.scrollTop,
        scrollHeight: chatContainer.scrollHeight
      }
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100
      
      // Track if user is actively scrolling
      isUserScrollingRef.current = !isAtBottom
      setAutoScrollEnabled(isAtBottom)
      
      // Preserve current position
      preserveScrollPosition()
    }

    // Preserve position before potential re-renders
    const handleBeforeUnload = preserveScrollPosition
    
    chatContainer.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('beforeunload', handleBeforeUnload)
    
    return () => {
      chatContainer.removeEventListener('scroll', handleScroll)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  // Restore scroll position after re-renders if user was not at bottom
  useEffect(() => {
    if (scrollPositionRef.current && isUserScrollingRef.current) {
      const chatContainer = document.querySelector('.overflow-y-auto')
      if (chatContainer && scrollPositionRef.current) {
        const { scrollTop, scrollHeight } = scrollPositionRef.current
        const currentScrollHeight = chatContainer.scrollHeight
        
        // If content height changed, adjust scroll position proportionally
        if (currentScrollHeight !== scrollHeight && scrollHeight > 0) {
          const scrollRatio = scrollTop / scrollHeight
          chatContainer.scrollTop = scrollRatio * currentScrollHeight
        } else {
          chatContainer.scrollTop = scrollTop
        }
      }
    }
  })
  
  if (!messages || messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔬</div>
          <h2 className="text-2xl font-bold text-gray-700 mb-2">
            Welcome to Research Agent
          </h2>
          <p className="text-gray-500">
            Ask any research question to get started
          </p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="space-y-4">
      {messages.map((message, index) => (
        <div key={`${message.id}-${index}`}>
          {/* Regular message */}
          <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-4xl rounded-lg px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-primary text-primary-content'
                  : 'bg-base-100 border'
              }`}
            >
              {message.role === 'user' ? (
                <div className="whitespace-pre-wrap">
                  {message.content}
                </div>
              ) : (
                <div>
                  {message.isStreaming && !message.content ? (
                    <div className="flex items-center space-x-2">
                      <div className="loading loading-dots loading-sm"></div>
                      <span className="text-sm text-gray-500">
                        Research in progress...
                      </span>
                    </div>
                  ) : (
                    <MarkdownWithCitations content={message.content} citations={message.citations} />
                  )}
                  
                  {message.citations && message.citations.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="font-semibold text-sm mb-2">📚 Sources:</h4>
                      <div className="max-h-80 overflow-y-auto border rounded-lg bg-gray-50 p-3">
                        <div className="space-y-3">
                          {message.citations.map((citation, index) => (
                            <div key={index} className="text-sm text-gray-600 flex items-start space-x-2 p-2 bg-white rounded border">
                              <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-blue-800 bg-blue-100 rounded-full border border-blue-200 flex-shrink-0">
                                {citation.number || index + 1}
                              </span>
                              <div className="flex-1">
                                {citation.url ? (
                                  <a 
                                    href={citation.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                  >
                                    {citation.title || citation.url}
                                  </a>
                                ) : (
                                  <span className="font-medium">{citation.title}</span>
                                )}
                                {citation.source && citation.source !== 'Unknown' && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Source: {formatSourceName(citation.source, citation.type)}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Research Summary for completed assistant messages */}
                  {message.role === 'assistant' && 
                   !message.isStreaming && 
                   message.metadata?.researchSummary && (
                    <ResearchSummary researchData={message.metadata.researchSummary} />
                  )}
                  
                  {message.status === 'error' && (
                    <div className="mt-2 p-2 bg-error bg-opacity-10 text-error rounded">
                      <div className="text-sm">
                        Failed to get response. Please try again.
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className={`text-xs mt-2 ${
                message.role === 'user' ? 'text-white text-opacity-80' : 'text-gray-500'
              }`}>
                {new Date(message.timestamp).toLocaleTimeString()}
                {message.status === 'sending' && (
                  <span className="ml-2">
                    <div className="loading loading-spinner loading-xs"></div>
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Show stream widgets after the last assistant message if it's streaming OR if it has final answer data */}
          {message.role === 'assistant' && index === messages.length - 1 && streamData && Object.keys(streamData).length > 0 && (
            message.isStreaming || streamData.final_answer
          ) && (
            <div className="mt-2">
              <StreamWidgets 
                data={streamData} 
                loading={loading} 
                wsStatus={wsStatus} 
              />
            </div>
          )}
        </div>
      ))}
      
      {/* WebSocket Error Display */}
      {wsError && wsStatus === 'failed' && (
        <div className="flex justify-start">
          <div className="max-w-4xl rounded-lg px-4 py-3 bg-error bg-opacity-10 border border-error">
            <div className="flex items-center space-x-2">
              <span className="text-error">⚠️</span>
              <span className="text-sm text-error">
                Connection error: {wsError.message || 'Failed to connect to research service'}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* WebSocket Reconnecting Display */}
      {wsStatus === 'reconnecting' && (
        <div className="flex justify-start">
          <div className="max-w-4xl rounded-lg px-4 py-3 bg-warning bg-opacity-10 border border-warning">
            <div className="flex items-center space-x-2">
              <div className="loading loading-spinner loading-xs"></div>
              <span className="text-sm text-warning">
                Reconnecting to research service...
              </span>
            </div>
          </div>
        </div>
      )}
      
      {loading && messages.length === 0 && (
        <div className="flex justify-start">
          <div className="max-w-4xl rounded-lg px-4 py-3 bg-base-100 border">
            <div className="flex items-center space-x-2" data-research-progress>
              <div className="loading loading-dots loading-sm"></div>
              <span className="text-sm text-gray-500">
                Research in progress...
              </span>
            </div>
          </div>
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  )
}